import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SpanishFlag from './spanish.png';
import EnglishFlag from './english.png';
import PortugueseFlag from './portugese.png';
import './languageSelector.scss';
import { useLanguage } from '../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../utils/createScopedI18n';
import { mainLayoutI18n } from '../../layout/MainLayout/i18n';

/**
 * LanguageSelector
 *
 * Small dropdown for the top navigation bar.
 * - Shows the current language as a flag + label (by default: Español).
 * - On click, opens a custom dropdown with the 3 language options.
 * - The currently selected option appears as selected in the list.
 *
 * Props:
 * - DesignSystemComponents: object with our DS components (expects `SvgIcon`).
 * - value: initial language code ("es" | "en" | "pt"). Defaults to "es".
 * - onChange: callback(languageCode, option) when language changes.
 */
export default function LanguageSelector({
  DesignSystemComponents,
  value = 'es',
  onChange,
}) {
  const { SvgIcon } = DesignSystemComponents || {};
  const { language } = useLanguage();
  const mainLayoutLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: mainLayoutI18n });

  useEffect(() => {
    mainLayoutI18n.changeLanguage(mainLayoutLanguage);
  }, [mainLayoutLanguage]);

  const languageOptions = useMemo(
    () => [
      { label: t('language_selector_es'), value: 'es', flag: SpanishFlag },
      { label: t('language_selector_en'), value: 'en', flag: EnglishFlag },
      { label: t('language_selector_pt'), value: 'pt', flag: PortugueseFlag },
    ],
    [t]
  );

  const initialOption = useMemo(
    () =>
      languageOptions.find((opt) => opt.value === value) || languageOptions[0],
    [languageOptions, value]
  );

  const [selected, setSelected] = useState(initialOption);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setSelected(initialOption);
  }, [initialOption]);

  const toggleOpen = () => {
    setIsOpen((prev) => !prev);
  };

  const handleSelect = (option) => {
    setSelected(option);
    setIsOpen(false);
    if (onChange) {
      onChange(option.value, option);
    }
  };

  return (
    <div className={`language-selector ${isOpen ? 'is-open' : ''}`}>
      <button
        type='button'
        className='language-selector__trigger'
        onClick={toggleOpen}
        aria-label={selected.label}
      >
        <img
          src={selected.flag}
          alt={selected.label}
          className='language-selector__flag'
          style={{
            width: 24,
            height: 24,
          }}
        />
        {SvgIcon ? (
          <SvgIcon
            name={isOpen ? 'chevronUp' : 'chevronDown'}
            size='16px'
            stroke='var(--font-light)'
            className='language-selector__chevron'
          />
        ) : (
          <span className='language-selector__chevron'>
            {isOpen ? '▲' : '▼'}
          </span>
        )}
      </button>

      {isOpen && (
        <div className='language-selector__menu'>
          {languageOptions.map((opt) => (
            <button
              key={opt.value}
              type='button'
              className={`language-selector__option ${
                opt.value === selected.value ? 'is-selected' : ''
              }`}
              onClick={() => handleSelect(opt)}
            >
              <img
                src={opt.flag}
                alt={opt.label}
                className='language-selector__option-flag'
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  objectFit: 'cover',
                }}
              />
              <span className='language-selector__option-label'>
                {opt.label}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
